/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import afterSetExtremes from "components/charts/modern/highcharts/afterSetExtremes";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LogLinearScaleSelector } from "components/charts/modern/inputs/AxisScaleSelector";
import ChartPeriodSelector, { ChartPeriodId } from "components/charts/modern/inputs/ChartPeriodSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import ChartOverlaySelect from "components/charts/modern/overlay/ChartOverlaySelect";
import IssuersSelect from "components/charts/modern/overlay/IssuersSelect/IssuersSelect";
import SelectedIssuersStack from "components/charts/modern/overlay/IssuersSelect/SelectedIssuersStack";
import { SELECTED_ISSUERS_ATOM } from "components/charts/modern/overlay/IssuersSelect/atoms";
import earningsFlagsSeries from "components/charts/modern/overlay/earningsFlagsSeries";
import labelOnlyEventSeries from "components/charts/modern/overlay/labelOnlyEventSeries";
import type { ChartOverlay } from "components/charts/modern/overlay/types";
import useChartOverlays, { isEarningOverlayKind } from "components/charts/modern/overlay/useChartOverlays";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import type { ColorTheme } from "components/charts/modern/quotes/getColorThemeForIndex";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import QuotesChartTooltip from "components/charts/modern/tooltip/QuotesChartTooltip";
import Avatar from "components/core/Avatar";
import LocaleContext from "components/i18n/LocaleContext";
import { Button } from "components/ui/Button";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import classNames from "components/ui/classNames";
import aeonik from "components/utils/aeonik";
import { startOfYear } from "date-fns";
import addWeeks from "date-fns/addWeeks";
import compareAsc from "date-fns/compareAsc";
import isAfter from "date-fns/isAfter";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import subMonths from "date-fns/subMonths";
import subYears from "date-fns/subYears";
import type Highcharts from "highcharts/highstock";
import type {
  YAxisPlotLinesOptions,
  SeriesAreaOptions,
  SeriesFlagsOptions,
  SeriesLineOptions,
  DashStyleValue,
  AlignValue,
} from "highcharts/highstock";
import { useAtomValue } from "jotai";
import QuotesHistoryQuery from "lib/graphql/queries/QuotesHistoryQuery";
import type {
  IQuotesHistoryQuery,
  IQuotesHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/QuotesHistoryQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import flatten from "lodash/fp/flatten";
import { FoldVerticalIcon } from "lucide-react";
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import { IIssuerListingCountry } from "types/graphqlTypes";
import { useLocalStorage } from "usehooks-ts";
import calculateMarketReturns from "components/charts/modern/quotes/calculateMarketReturns";
import ReturnBox from "components/charts/modern/quotes/ReturnBox";

interface QuotesChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function QuotesChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: QuotesChartProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const issuer = useRootIssuer();
  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);
  const locale = useContext(LocaleContext);
  const [axisScaleType, setAxisScaleType] = useState<AxisScaleType>("linear");
  const [isPlotQuotesLimit, setIsPlotQuotesLimit] = useLocalStorage("plotQuotesLimit", false);
  const [disableTemporaryQuotesLimit, setDisableTemporaryQuotesLimit] = useState(false);
  const periods = [
    {
      period: ChartPeriodId.P1M,
      label: intl.formatMessage({
        defaultMessage: "1M",
        description: "Período do gráfico de cotação",
        id: "SYUwn6",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "1 mês",
        description: "Período do gráfico de cotação",
        id: "q9yRjj",
      }),
      from: subMonths(new Date(), 1),
    },
    {
      period: ChartPeriodId.P3M,
      label: intl.formatMessage({
        defaultMessage: "3M",
        description: "Período do gráfico de cotação",
        id: "Sbe6Sk",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "3 meses",
        description: "Período do gráfico de cotação",
        id: "/6QzwO",
      }),
      from: subMonths(new Date(), 3),
    },
    {
      period: ChartPeriodId.P6M,
      label: intl.formatMessage({
        defaultMessage: "6M",
        description: "Período do gráfico de cotação",
        id: "nnEvbr",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "6 meses",
        description: "Período do gráfico de cotação",
        id: "fs9wFD",
      }),
      from: subMonths(new Date(), 6),
    },
    {
      period: ChartPeriodId.YTD,
      label: intl.formatMessage({
        defaultMessage: "Ano",
        description: "Período do gráfico de cotação",
        id: "l7+hs7",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "Ano",
        description: "Período do gráfico de cotação",
        id: "l7+hs7",
      }),
      from: startOfYear(new Date()),
      to: new Date(),
      // Only available if 1 week away of the start of the year
      disabled: isAfter(addWeeks(startOfYear(new Date()), 1), new Date()),
    },
    {
      period: ChartPeriodId.P1Y,
      label: intl.formatMessage({
        defaultMessage: "1A",
        description: "Período do gráfico de cotação",
        id: "a6S7oJ",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "1 ano",
        description: "Período do gráfico de cotação",
        id: "J5oq/3",
      }),
      from: subYears(new Date(), 1),
    },
    {
      period: ChartPeriodId.P5Y,
      label: intl.formatMessage({
        defaultMessage: "5A",
        description: "Período do gráfico de cotação",
        id: "wlaelQ",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "5 anos",
        description: "Período do gráfico de cotação",
        id: "PNaRTF",
      }),
      from: subYears(new Date(), 5),
    },
    {
      period: ChartPeriodId.P10Y,
      label: intl.formatMessage({
        defaultMessage: "10A",
        description: "Período do gráfico de cotação",
        id: "OdRUhz",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "10 anos",
        description: "Período do gráfico de cotação",
        id: "2R8NkV",
      }),
      from: subYears(new Date(), 10),
      disabled: !selfie?.isPremium,
    },
    {
      period: ChartPeriodId.AllTime,
      label: intl.formatMessage({
        defaultMessage: "Máx.",
        description: "Periodo do gráfico de cotação",
        id: "uy31XH",
      }),
      tooltipContent: intl.formatMessage({
        defaultMessage: "Todo Período",
        description: "Periodo do gráfico de cotação",
        id: "3cAdbV",
      }),
      from: parseISO("1960-01-01"),
      disabled: !selfie?.isPremium,
    },
  ];
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const period = periods.find(({ period }) => {
      if (selfie?.isPremium) {
        return period === ChartPeriodId.P10Y;
      }
      return period === ChartPeriodId.P5Y;
    });
    if (!period) {
      throw new Error("Couldn't locate the default period");
    }
    return {
      period: period.period,
      startDate: period.from,
      endDate: new Date(),
    };
  });
  const selectedIssuers = useAtomValue(SELECTED_ISSUERS_ATOM);

  const { overlays, selectedOverlayKind, setSelectedOverlayKind } = useChartOverlays();

  const tickerSymbolsForSeries = [...tickerSymbols].concat(selectedIssuers.map((node) => node.tickerSymbol));

  // Query
  const { data, previousData, loading } = useQuery<IQuotesHistoryQuery, IQuotesHistoryQueryVariables>(
    QuotesHistoryQuery,
    {
      variables: {
        tickerSymbols: [...new Set(tickerSymbolsForSeries)].slice(0, 5),
        startDate: lightFormat(selectedPeriod.startDate, "yyyy-MM-dd"),
        endDate: lightFormat(selectedPeriod.endDate, "yyyy-MM-dd"),
      },
    },
  );

  const quotesHistory = (loading && !data ? previousData?.quotesHistory : data?.quotesHistory) || [];

  const marketReturns = calculateMarketReturns(quotesHistory);

  useEffect(() => {
    if (!chartRef.current || !isPlotQuotesLimit || axisScaleType !== "logarithmic") {
      return;
    }

    chartRef.current.chart.yAxis.forEach((axis) => {
      axis.removePlotLine("min");
      axis.removePlotLine("max");
    });

    setDisableTemporaryQuotesLimit(false);
  }, [chartRef.current, isPlotQuotesLimit, axisScaleType]);

  useEffect(() => {
    resetZoom();
  }, [quotesHistory]);

  const resetZoom = useCallback(() => {
    if (!chartRef.current) {
      return;
    }

    chartRef.current.chart.yAxis.forEach((axis) => {
      const points = flatten(axis.series.map((series) => series.points));

      const values = points.map((node) => node.y).filter(notUndefinedOrNull);

      const minValue = Math.min(...values);
      const maxValue = Math.max(...values);
      const currentExtremes = axis.getExtremes();

      if (minValue === currentExtremes.min && maxValue === currentExtremes.max) {
        return;
      }

      axis.setExtremes(minValue, maxValue);
    });
  }, [chartRef.current]);

  const series: (SeriesAreaOptions | SeriesLineOptions | SeriesFlagsOptions)[] = quotesHistory.map((series, index) => {
    let selectedOverlay: ChartOverlay | null = null;
    if (overlays && overlays.length > 0 && quotesHistory.length === 1 && series.tickerSymbol && selectedOverlayKind) {
      selectedOverlay = overlays.find((overlay) => overlay.kind === selectedOverlayKind) || null;
    }

    let theme: ColorTheme = getColorThemeForIndex(index);

    if (quotesHistory.length === 1) {
      const [quotes] = quotesHistory;

      if (quotes.nodes.length > 0) {
        const firstQuote = quotes.nodes[0];
        const lastQuote = quotes.nodes[quotes.nodes.length - 1];

        if (firstQuote.close <= lastQuote.close) {
          theme = getColorThemeForIndex(1);
        } else {
          theme = {
            color: colors.red[50],
            lineColor: colors.red[600],
          };
        }
      }
    }

    const options: SeriesAreaOptions = {
      showInNavigator: index === 0,
      id: series.tickerSymbol,
      type: "area",
      animation: false,
      name: series.tickerSymbol,
      color: theme.color,
      lineColor: theme.lineColor,
      lineWidth: 2,
      data: series.nodes
        .map((node) => [parseISO(node.date).getTime(), node.close])
        .sort((a, b) => compareAsc(a[0], b[0])),
      custom: {
        tickerSymbol: series.tickerSymbol,
        primaryColor: theme.color,
        secondaryColor: theme.lineColor,
        overlay: selectedOverlay,
      },
      marker: {
        width: 8,
        height: 8,
        lineWidth: 1,
        fillColor: theme.color,
        color: theme.color,
        lineColor: theme.lineColor,
        symbol: "circle",
      },
    };
    return options;
  });

  if (overlays && overlays.length > 0 && series.length > 0 && series[0].id && selectedOverlayKind) {
    const selectedOverlay = overlays.find((overlay) => overlay.kind === selectedOverlayKind);

    if (selectedOverlay && isEarningOverlayKind(selectedOverlayKind)) {
      series.push(earningsFlagsSeries(intl, series[0].id, selectedOverlay, parseISO(quotesHistory[0].nodes[0].date)));
    } else if (selectedOverlay) {
      series.push(labelOnlyEventSeries(series[0].id, selectedOverlay, parseISO(quotesHistory[0].nodes[0].date)));
    }
  }
  const minValues = quotesHistory.map((series) => Math.min(...series.nodes.map((node) => node.close)));
  const maxValues = quotesHistory.map((series) => Math.max(...series.nodes.map((node) => node.close)));
  const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;
  const maxValue = maxValues.length > 0 ? Math.min(...maxValues) : 0;

  const currentQuote = quotesHistory.length > 0 ? quotesHistory[0].nodes[quotesHistory[0].nodes.length - 1].close : 0;

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
      events: {
        render() {
          const chart = this as Highcharts.Chart;

          const plotLines: YAxisPlotLinesOptions[] | undefined =
            isPlotQuotesLimit && !disableTemporaryQuotesLimit && quotesHistory.length > 0
              ? flatten(
                  quotesHistory.map((node, index) => {
                    const series = chart.series.find((serie) => serie.name === node.tickerSymbol);

                    if (!series) {
                      return null;
                    }

                    let theme: ColorTheme = getColorThemeForIndex(index);

                    const firstQuote = series.points[0].y;
                    const lastQuote = series.points[series.points.length - 1].y;

                    if (firstQuote && lastQuote && firstQuote >= lastQuote) {
                      theme = {
                        color: colors.red[50],
                        lineColor: colors.red[600],
                      };
                    }

                    if (chart.yAxis.length > 0) {
                      chart.yAxis[0].removePlotLine(`min`);
                      chart.yAxis[0].removePlotLine(`max`);
                    }

                    const minValue = Math.min(...series.points.map((node) => node.y).filter(notUndefinedOrNull));
                    const maxValue = Math.max(...series.points.map((node) => node.y).filter(notUndefinedOrNull));

                    return [
                      {
                        id: `min`,
                        color: theme.lineColor,
                        value: minValue,
                        dashStyle: "Dash" as DashStyleValue,
                        width: 1,
                        zIndex: 4,
                        label: {
                          enabled: true,
                          align: "center" as AlignValue,
                          text: `Mín: ${minValue}`,
                          style: {
                            color: colors.slate[500],
                            fontFamily: aeonik.style.fontFamily,
                            fontWeight: "500",
                            fontSize: "12px",
                          },
                        },
                      },
                      {
                        id: `max`,
                        color: theme.lineColor,
                        value: maxValue,
                        dashStyle: "Dash" as DashStyleValue,
                        width: 1,
                        zIndex: 4,
                        label: {
                          enabled: true,
                          align: "center" as AlignValue,
                          text: `Máx: ${maxValue}`,
                          style: {
                            color: colors.slate[500],
                            fontFamily: aeonik.style.fontFamily,
                            fontWeight: "500",
                            fontSize: "12px",
                          },
                        },
                      },
                    ];
                  }),
                ).filter(notUndefinedOrNull)
              : undefined;

          if (plotLines && chart.yAxis.length > 0) {
            plotLines.forEach((node) => {
              chart.yAxis[0].addPlotLine(node);
            });
          }
        },
      },
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      events: {
        afterSetExtremes(this, event) {
          afterSetExtremes(this, event);
        },
      },
    },
    yAxis: {
      min: minValue,
      max: maxValue,
      type: axisScaleType,
      scrollbar: {
        enabled: false,
      },
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      labels: {
        enabled: true,
        align: "center",
        formatter() {
          // Verifica se o rótulo atual é o valor que você deseja destacar
          if (this.value === currentQuote) {
            return `<span style="color:${colors.purple[500]};font-weight:bold;">${this.value}</span>`;
          }
          return String(this.value);
        },
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      tickWidth: 0,
      showFirstLabel: false,
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...navigatorStyles,
      yAxis: {
        ...navigatorStyles.yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        min: minValue,
        max: maxValue,
        type: axisScaleType,
        zoomEnabled: false,
      },
    },
    series,
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter() {
        return renderToStaticMarkup(<QuotesChartTooltip locale={locale} context={this} />);
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        dataGrouping: {
          enabled: true,
          units: [["week", [1]]],
          approximation: (groupData: number[]) => {
            return groupData[groupData.length - 1];
          },
        },
        states: {
          hover: {
            lineWidth: 2,
          },
        },
      },
      flags: {
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
      area: {
        color: colors.purple[50],
        lineColor: colors.purple[600],
        lineWidth: 2,
        marker: {
          enabled: false,
          fillColor: colors.emerald[50],
          lineColor: colors.emerald[600],
          lineWidth: 2,
          width: 8,
          height: 8,
        },
      },
    },
  };

  return (
    <div className="flex h-full w-full flex-col space-y-4">
      <div className="relative flex flex-col items-center justify-between sm:flex-row">
        <div className="grid w-full grid-cols-3 items-center gap-4 md:flex">
          {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
            <div className="col-span-3">
              <TickerSymbolSelector
                type="multiple"
                tickerSymbols={availableTickerSymbols}
                value={tickerSymbols}
                onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
              />
            </div>
          )}

          <ChartPeriodSelector
            periods={periods}
            value={selectedPeriod.period}
            onChange={(period, startDate, endDate) => {
              if (chartRef.current) {
                chartRef.current.chart.xAxis.forEach((axis) => {
                  axis.setExtremes();
                });
              }

              setSelectedPeriod({
                period,
                startDate,
                endDate,
              });
            }}
          />

          <div className="col-span-2 flex gap-4 md:contents">
            <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />

            <button
              type="button"
              disabled={quotesHistory.length > 1 || axisScaleType === "logarithmic"}
              className={classNames(
                "rounded-xl border border-slate-200 bg-slate-50/60 p-2 text-slate-600",
                "relative dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                {
                  "bg-violet-50 text-violet-600 dark:bg-violet-600/20 dark:text-violet-200":
                    isPlotQuotesLimit && !disableTemporaryQuotesLimit,
                },
                "focus-visible:outline-none",
                "focus-visible:ring-1 focus-visible:ring-offset-1",
                "focus-visible:ring-gray-100 focus-visible:ring-offset-gray-300",
                "focus-visible:border-transparent focus-visible:bg-gray-50 focus-visible:text-gray-600",
                "dark:focus-visible:ring-slate-950 dark:focus-visible:ring-offset-transparent",
                "disabled:cursor-not-allowed",
              )}
              onClick={() => {
                if (isPlotQuotesLimit && chartRef.current) {
                  chartRef.current.chart.yAxis.forEach((axis) => {
                    axis.removePlotLine("min");
                    axis.removePlotLine("max");
                  });
                }

                setIsPlotQuotesLimit((previous) => !previous);
              }}
            >
              <FoldVerticalIcon />
            </button>
          </div>

          {issuer.__typename !== "BrazilianRealEstateFund" && (
            <ChartOverlaySelect
              onChange={setSelectedOverlayKind}
              value={selectedOverlayKind}
              overlays={overlays}
              disabled={false}
            />
          )}

          {issuer.__typename === "AmericanCompanyIssuer" && (
            <IssuersSelect
              title="Search for companies"
              description="Type a symbol to perform a quick search..."
              inputSearchPlaceholder="For example: AAPL or NVDA"
              inputSearchNoResultsText="No results"
              issuerId={issuer._id}
              listingCountry={
                issuer.__typename === "AmericanCompanyIssuer" ? IIssuerListingCountry.Us : IIssuerListingCountry.Br
              }
            >
              <Button
                variant="outline"
                className={classNames(
                  "col-span-3 row-start-1",
                  "w-full text-slate-600 hover:bg-slate-100 sm:max-w-xs lg:w-auto",
                  "disabled:pointer-events-auto disabled:cursor-not-allowed",
                )}
                disabled={!(selfie && selfie.isPremium)}
              >
                {selectedIssuers.length > 0 ? `Compare (${selectedIssuers.length})` : "Compare"}
              </Button>
            </IssuersSelect>
          )}
        </div>

        <div className="hidden items-center space-x-2 sm:inline-flex">
          <SimpleTextTooltip content={issuer.name}>
            <Avatar
              src={issuer.__typename !== "BrazilianRealEstateFund" && issuer.niceLogo ? issuer.niceLogo.url : null}
              hashValue={issuer._id}
              alt={issuer.name}
              size={40}
              squircle={true}
            />
          </SimpleTextTooltip>

          <SelectedIssuersStack isLoading={loading} />
        </div>
      </div>

      <HighchartsChart ref={chartRef} options={options} className="h-96 min-h-96 md:h-full md:max-h-[466px]" />

      <div className="grid grid-cols-2 gap-4 md:flex md:flex-row">
        {marketReturns.map((node, index) => {
          const theme = getColorThemeForIndex(index);
          return (
            <Fragment key={node.tickerSymbol}>
              <ReturnBox
                title={intl.formatMessage({ defaultMessage: "% Total", id: "vccktX" })}
                tickerSymbol={node.tickerSymbol}
                firstPoint={node.firstPoint}
                lastPoint={node.lastPoint}
                returnRate={node.totalReturn}
                textColor={theme.lineColor}
                className="bg-slate-50"
                tickerSymbolClassName="bg-slate-400/10 text-slate-800"
              />

              {node.annualReturn && (
                <ReturnBox
                  title={intl.formatMessage({ defaultMessage: "Retorno Anual", id: "Z8+BV4" })}
                  tickerSymbol={node.tickerSymbol}
                  firstPoint={node.firstPoint}
                  lastPoint={node.lastPoint}
                  returnRate={node.annualReturn}
                  textColor={theme.lineColor}
                  className="bg-sky-50"
                  tickerSymbolClassName="bg-sky-400/10 text-sky-800"
                />
              )}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}
